import { createContext, type ReactNode, useContext } from "react";
import { createAuctionQueryOptionsFactory } from "@/queries/auction.ts";
import { createAuctionSessionQueryOptionsFactory } from "@/queries/auction-session.ts";
import { createRegistrationQueryOptionsFactory } from "@/queries/registration.ts";
import { createReviewQueryOptionsFactory } from "@/queries/review.js";
import { createReviewRequestQueryOptionsFactory } from "@/queries/review-request.js";

export const createQueryOptionsFactory = (authFetch: typeof fetch) => ({
    auction: createAuctionQueryOptionsFactory(authFetch),
    auctionSession: createAuctionSessionQueryOptionsFactory(authFetch),
    registration: createRegistrationQueryOptionsFactory(authFetch),
    review: createReviewQueryOptionsFactory(authFetch),
    reviewRequest: createReviewRequestQueryOptionsFactory(authFetch),
});

export type QueryOptionsFactory = ReturnType<typeof createQueryOptionsFactory>;

const QueryOptionsFactoryContext = createContext<QueryOptionsFactory | null>(null);

type QueryOptionsFactoryProviderProps = {
    children: ReactNode;
    factory: QueryOptionsFactory;
};

export const QueryOptionsFactoryProvider = ({
    children,
    factory,
}: QueryOptionsFactoryProviderProps): ReactNode => (
    <QueryOptionsFactoryContext.Provider value={factory}>
        {children}
    </QueryOptionsFactoryContext.Provider>
);

export const useQueryOptionsFactory = (): QueryOptionsFactory => {
    const factory = useContext(QueryOptionsFactoryContext);

    if (!factory) {
        throw new Error("useQueryOptionsFactory used outside QueryOptionsFactoryProvider");
    }

    return factory;
};
